import React, { useState, useCallback, useMemo, useEffect, useRef } from "react"
import { Map, Marker, GoogleApiWrapper } from "google-maps-react"
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Row,
    Col,
    FormGroup,
    Label,
    Input,
    FormFeedback,
} from "reactstrap"
import CButton from "components/Common/CButton"
import PropTypes from "prop-types"

const LoadingContainer = () => <div>Loading map...</div>

const GoogleMapLocationPicker = ({
    google,
    isOpen,
    toggle,
    title = "Select Location",
    onLocationSelect,
    currentLocation = null,
    appInfo = null,
}) => {
    const [selectedLocation, setSelectedLocation] = useState(currentLocation)
    const [latInput, setLatInput] = useState(currentLocation?.posLat || "")
    const [lngInput, setLngInput] = useState(currentLocation?.posLong || "")
    const [latError, setLatError] = useState("")
    const [lngError, setLngError] = useState("")
    const debounceTimerRef = useRef(null)
    const mapRef = useRef(null)

    // Dynamic bounds and center based on app country
    const getCountryData = useMemo(() => {
        

        // Find country by appCountryID
        const country = appInfo?.countries?.find(c => c.countryID === appInfo.appCountryID)
        
        if (country?.mbr) {
            // Parse MBR string format: "north,south,east,west"
            const mbrValues = country.mbr.split(',').map(parseFloat)
            if (mbrValues.length === 4 && mbrValues.every(val => !isNaN(val))) {
                const [north, south, east, west] = mbrValues
                
                return {
                    center: {
                        lat: (north + south) / 2,
                        lng: (east + west) / 2
                    },
                    bounds: {
                        north,
                        south,
                        east,
                        west
                    }
                }
            }
        }

        // Fallback to UAE if country not found or no MBR data
        return {
            center: { lat: 23.4241, lng: 53.8478 },
            bounds: {
                north: 26.662696,
                south: 22.216723,
                east: 56.742478,
                west: 51.125153
            }
        }
    }, [appInfo])

    // Validation functions
    const isValidLatitude = useCallback((lat) => {
        const num = parseFloat(lat)
        if (isNaN(num) || num < -90 || num > 90) {
            return false
        }

        // Check against country bounds if available
        if (getCountryData?.bounds) {
            const { north, south } = getCountryData.bounds
            if (num < south || num > north) {
                return false
            }
        }

        return true
    }, [getCountryData])

    const isValidLongitude = useCallback((lng) => {
        const num = parseFloat(lng)
        if (isNaN(num) || num < -180 || num > 180) {
            return false
        }

        // Check against country bounds if available
        if (getCountryData?.bounds) {
            const { east, west } = getCountryData.bounds
            if (num < west || num > east) {
                return false
            }
        }

        return true
    }, [getCountryData])

    // Sync input fields when currentLocation changes (e.g., modal reopens)
    useEffect(() => {
        if (currentLocation?.posLat && currentLocation?.posLong) {
            setLatInput(currentLocation.posLat)
            setLngInput(currentLocation.posLong)
            setSelectedLocation(currentLocation)
        } else {
            setLatInput("")
            setLngInput("")
            setSelectedLocation(null)
        }
        setLatError("")
        setLngError("")
    }, [currentLocation, isOpen])

    // Calculate map center and zoom based on current location
    const mapCenter = useMemo(() => {
        if (currentLocation?.posLat && currentLocation?.posLong) {
            const lat = parseFloat(currentLocation.posLat)
            const lng = parseFloat(currentLocation.posLong)
            
            // Validate coordinates
            if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
               
                return { lat, lng }
            }
        }
       
        return getCountryData.center
    }, [currentLocation, getCountryData.center])

    const initialZoom = useMemo(() => {
        return currentLocation?.posLat && currentLocation?.posLong ? 12 : 10
    }, [currentLocation])

    // Handle map ready - set country bounds only for new locations
    const handleMapReady = useCallback((_, map) => {
        if (map) {
            mapRef.current = map
            if (!currentLocation) {
                // No current location - fit to country bounds
                const bounds = new window.google.maps.LatLngBounds()
                const countryBounds = getCountryData.bounds
                bounds.extend({ lat: countryBounds.north, lng: countryBounds.east })
                bounds.extend({ lat: countryBounds.south, lng: countryBounds.west })
                map.fitBounds(bounds)
            } else {
                // Has current location - ensure map centers on i
                map.setCenter(mapCenter)
                map.setZoom(12) // Use a reasonable zoom level for editing
            }
        }
    }, [currentLocation, getCountryData.bounds, mapCenter, initialZoom])

    // Handle map click to set location
    const handleMapClick = useCallback((_, __, clickEvent) => {
        const lat = clickEvent.latLng.lat()
        const lng = clickEvent.latLng.lng()
        setSelectedLocation({
            posLat: lat,
            posLong: lng
        })
        setLatInput(lat)
        setLngInput(lng)
        setLatError("")
        setLngError("")
    }, [])

    // Handle marker drag
    const handleMarkerDrag = useCallback((_, __, event) => {
        const lat = event.latLng.lat()
        const lng = event.latLng.lng()
        setSelectedLocation({
            posLat: lat,
            posLong: lng
        })
        setLatInput(lat)
        setLngInput(lng)
        setLatError("")
        setLngError("")
    }, [])

    // Handle latitude input change
    const handleLatInputChange = useCallback((e) => {
        const value = e.target.value
        setLatInput(value)

        // Clear previous debounce timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        // Validate
        if (value === "") {
            setLatError("Latitude is required")
            return
        }

        const num = parseFloat(value)
        if (isNaN(num) || num < -90 || num > 90) {
            setLatError("Latitude must be between -90 and 90")
            return
        }

        // Check country bounds
        if (getCountryData?.bounds) {
            const { north, south } = getCountryData.bounds
            if (num < south || num > north) {
                setLatError(`Latitude must be within country bounds (${south.toFixed(2)} to ${north.toFixed(2)})`)
                return
            }
        }

        setLatError("")

        // Debounce map update
        debounceTimerRef.current = setTimeout(() => {
            const lat = parseFloat(value)
            const lng = lngInput ? parseFloat(lngInput) : null

            if (!isNaN(lat) && lng !== null && !isNaN(lng)) {
                setSelectedLocation({
                    posLat: lat,
                    posLong: lng
                })

                // Update map center
                if (mapRef.current) {
                    mapRef.current.panTo({ lat, lng })
                    mapRef.current.setZoom(12)
                }
            }
        }, 300)
    }, [lngInput, getCountryData])

    // Handle longitude input change
    const handleLngInputChange = useCallback((e) => {
        const value = e.target.value
        setLngInput(value)

        // Clear previous debounce timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        // Validate
        if (value === "") {
            setLngError("Longitude is required")
            return
        }

        const num = parseFloat(value)
        if (isNaN(num) || num < -180 || num > 180) {
            setLngError("Longitude must be between -180 and 180")
            return
        }

        // Check country bounds
        if (getCountryData?.bounds) {
            const { east, west } = getCountryData.bounds
            if (num < west || num > east) {
                setLngError(`Longitude must be within country bounds (${west.toFixed(2)} to ${east.toFixed(2)})`)
                return
            }
        }

        setLngError("")

        // Debounce map update
        debounceTimerRef.current = setTimeout(() => {
            const lat = latInput ? parseFloat(latInput) : null
            const lng = parseFloat(value)

            if (lat !== null && !isNaN(lat) && !isNaN(lng)) {
                setSelectedLocation({
                    posLat: lat,
                    posLong: lng
                })

                // Update map center
                if (mapRef.current) {
                    mapRef.current.panTo({ lat, lng })
                    mapRef.current.setZoom(12)
                }
            }
        }, 300)
    }, [latInput, getCountryData])

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
        }
    }, [])

    // Check if inputs are valid for confirmation
    const isValidForConfirmation = useMemo(() => {
        return (
            latInput !== "" &&
            lngInput !== "" &&
            !latError &&
            !lngError &&
            isValidLatitude(latInput) &&
            isValidLongitude(lngInput)
        )
    }, [latInput, lngInput, latError, lngError, isValidLatitude, isValidLongitude])

    // Handle confirm selection
    const handleConfirmSelection = useCallback(() => {
        if (selectedLocation && onLocationSelect && isValidForConfirmation) {
            onLocationSelect(selectedLocation)
        }
        toggle()
    }, [selectedLocation, onLocationSelect, toggle, isValidForConfirmation])

    // Marker position - also consider currentLocation for initial display
    const markerPosition = useMemo(() => {
        const location = selectedLocation || currentLocation
        if (location?.posLat && location?.posLong && location.posLat !== "" && location.posLong !== "") {
            return {
                lat: parseFloat(location.posLat),
                lng: parseFloat(location.posLong)
            }
        }
        return null
    }, [selectedLocation, currentLocation])
    

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl">
            <ModalHeader toggle={toggle}>{title}</ModalHeader>
            <ModalBody>
                <Row className="mb-3">
                    <Col md="12">
                        <div className="text-muted">
                            Click on the map to set the merchant location. You can also drag the marker to fine-tune the position.
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col md="12">
                        <div style={{ height: "400px", width: "100%", position: "relative" }}>
                            <Map
                                google={google}
                                zoom={initialZoom}
                                style={{ width: "100%", height: "100%" }}
                                center={mapCenter}
                                onClick={handleMapClick}
                                onReady={handleMapReady}
                                containerStyle={{ position: "relative" }}
                            >
                                {markerPosition && (
                                    <Marker
                                        position={markerPosition}
                                        draggable={true}
                                        onDragend={handleMarkerDrag}
                                        title="Drag to adjust location"
                                    />
                                )}
                            </Map>
                        </div>
                    </Col>
                </Row>

                <Row className="mt-3">
                    <Col md="12">
                        <div className="bg-light p-3 rounded">
                            <div className="mb-2">
                                <strong>Manual Coordinate Entry</strong>
                                <div className="text-muted small">
                                    Enter coordinates manually or use the map above
                                </div>
                            </div>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>Latitude *</Label>
                                        <Input
                                            type="number"
                                            step="0.000001"
                                            placeholder="e.g., 25.276987"
                                            value={latInput}
                                            onChange={handleLatInputChange}
                                            invalid={!!latError}
                                        />
                                        {latError && (
                                            <FormFeedback>{latError}</FormFeedback>
                                        )}
                                        <div className="text-muted small mt-1">
                                            {getCountryData?.bounds
                                                ? `Valid range: ${getCountryData.bounds.south.toFixed(2)} to ${getCountryData.bounds.north.toFixed(2)}`
                                                : "Valid range: -90 to 90"
                                            }
                                        </div>
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>Longitude *</Label>
                                        <Input
                                            type="number"
                                            step="0.000001"
                                            placeholder="e.g., 55.296249"
                                            value={lngInput}
                                            onChange={handleLngInputChange}
                                            invalid={!!lngError}
                                        />
                                        {lngError && (
                                            <FormFeedback>{lngError}</FormFeedback>
                                        )}
                                        <div className="text-muted small mt-1">
                                            {getCountryData?.bounds
                                                ? `Valid range: ${getCountryData.bounds.west.toFixed(2)} to ${getCountryData.bounds.east.toFixed(2)}`
                                                : "Valid range: -180 to 180"
                                            }
                                        </div>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    </Col>
                </Row>

                {selectedLocation?.posLat && selectedLocation?.posLong && (
                    <Row className="mt-3">
                        <Col md="12">
                            <div className="alert alert-success mb-0">
                                <i className="bx bx-check-circle me-2"></i>
                                <strong>Selected Coordinates:</strong>
                                <span className="ms-2">
                                    Lat: {parseFloat(selectedLocation.posLat).toFixed(6)},
                                    Lng: {parseFloat(selectedLocation.posLong).toFixed(6)}
                                </span>
                            </div>
                        </Col>
                    </Row>
                )}
            </ModalBody>

            <ModalFooter>
                <CButton.Secondary text="Cancel" onClick={toggle} />
                <CButton.Primary
                    text="Confirm Location"
                    onClick={handleConfirmSelection}
                    disabled={!isValidForConfirmation}
                />
            </ModalFooter>
        </Modal>
    )
}

GoogleMapLocationPicker.propTypes = {
    google: PropTypes.object.isRequired,
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    title: PropTypes.string,
    onLocationSelect: PropTypes.func.isRequired,
    currentLocation: PropTypes.object,
    appInfo: PropTypes.object,
}

export default GoogleApiWrapper({
    apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    LoadingContainer: LoadingContainer,
    v: "3",
})(GoogleMapLocationPicker)